<template>
  <div
    class="cms-form-group"
    :class="{ 'cms-form-group-full': fullWidth, 'cms-disabled': disabled }"
  >
    <label v-if="label">{{ label }}</label>
    <div class="cms-date-range-list">
      <div
        v-if="modelValue.length"
        class="cms-date-range-columns"
        aria-hidden="true"
      >
        <span class="cms-date-range-col cms-date-range-col--trip">
          {{ $t('cms.activities.tripRangeColTrip') }}
        </span>
        <span class="cms-date-range-col">{{ $t('cms.activities.tripRangeStart') }}</span>
        <span class="cms-date-range-col" :title="endDateHelpText">
          {{ $t('cms.activities.tripRangeEnd') }}
        </span>
        <span class="cms-date-range-col">{{ $t('cms.activities.tripRangeColSingle') }}</span>
        <span class="cms-date-range-col">{{ $t('cms.activities.tripRangeColDouble') }}</span>
        <span class="cms-date-range-col">{{ $t('cms.activities.tripRangeColMaxPax') }}</span>
        <span class="cms-date-range-col">{{ $t('cms.activities.tripRangeColAvailable') }}</span>
        <span class="cms-date-range-col cms-date-range-col--guiders">
          {{ $t('cms.activities.tripRangeColGuiders') }}
        </span>
        <span class="cms-date-range-col cms-date-range-col--action" />
      </div>

      <article
        v-for="(range, index) in modelValue"
        :key="index"
        class="cms-date-range-row"
        :aria-label="$t('cms.activities.tripRangeLabel', { n: index + 1 })"
      >
        <span class="cms-date-range-row__trip">{{ index + 1 }}</span>
        <cms-date-input
          embedded
          :model-value="range.start_date"
          :label="$t('cms.activities.tripRangeStart')"
          :disabled="disabled"
          :required="required"
          :min-date="todayYmd"
          @update:model-value="updateStart(index, $event)"
        />
        <cms-date-input
          embedded
          :model-value="range.end_date"
          :label="$t('cms.activities.tripRangeEnd')"
          :disabled="true"
          :required="required"
        />
        <cms-input
          embedded
          :model-value="range.base_price_single_room"
          :label="$t('cms.activities.basePriceSingleRoom')"
          type="number"
          placeholder="0"
          :required="required"
          :disabled="disabled"
          @update:model-value="
            updateField(index, 'base_price_single_room', Number($event) || 0)
          "
        />
        <cms-input
          embedded
          :model-value="range.base_price_double_room"
          :label="$t('cms.activities.basePriceDoubleRoom')"
          type="number"
          placeholder="0"
          :required="required"
          :disabled="disabled"
          @update:model-value="
            updateField(index, 'base_price_double_room', Number($event) || 0)
          "
        />
        <cms-input
          embedded
          :model-value="range.max_pax"
          :label="$t('cms.activities.tripRangeMaxPax')"
          type="number"
          min="1"
          step="1"
          placeholder="0"
          :required="required"
          :disabled="disabled"
          @update:model-value="updateField(index, 'max_pax', Number($event) || 0)"
        />
        <span
          class="cms-date-range-available"
          :title="$t('cms.activities.tripRangeAvailableHint')"
        >
          {{ availableForRange(range) }}
        </span>
        <div class="cms-date-range-row__guiders">
          <label class="cms-date-range-sr-only">{{ $t('cms.activities.tripRangeGuiders') }}</label>
          <v-select
            :model-value="range.guider_ids"
            :options="guiderOptions"
            multiple
            label="label"
            :reduce="reduceGuiderOption"
            append-to-body
            class="cms-relationship-select cms-date-range-guider-select"
            :close-on-select="false"
            :clearable="true"
            :searchable="true"
            :disabled="disabled"
            @update:model-value="updateField(index, 'guider_ids', $event)"
          >
            <template #no-options>
              {{ $t('cms.guides.searchPlaceholder') }}
            </template>
          </v-select>
        </div>
        <cms-button
          variant="icon"
          icon="trash"
          class="cms-date-range-row__delete"
          :disabled="disabled || modelValue.length <= 1"
          :aria-label="$t('cms.activities.removeTripDates')"
          @click="removeRange(index)"
        />
      </article>

      <cms-button
        variant="secondary"
        icon="plus"
        :disabled="disabled"
        @click="addRange"
      >
        {{ $t('cms.activities.addTripDates') }}
      </cms-button>
    </div>
    <span v-if="helpText" class="cms-form-help">{{ helpText }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import CmsDateInput from './CmsDateInput.vue';
import CmsButton from './CmsButton.vue';
import CmsInput from './CmsInput.vue';
import type { CmsTripDateRange } from '@/types/cms';

export type { CmsTripDateRange } from '@/types/cms';

export interface GuiderSelectOption {
  label: string;
  value: number;
}

interface Props {
  modelValue: CmsTripDateRange[];
  label?: string;
  helpText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  duration?: number | string | null;
  guiderOptions?: GuiderSelectOption[];
  defaultSingleRoomPrice?: number;
  defaultDoubleRoomPrice?: number;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  disabled: false,
  required: false,
  guiderOptions: () => [],
  defaultSingleRoomPrice: 0,
  defaultDoubleRoomPrice: 0
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: CmsTripDateRange[]): void;
}>();

const { t } = useI18n();

const endDateHelpText = computed(() => {
  const days = parseDurationDays(props.duration);
  if (!days) {
    return t('cms.activities.tripRangeEndAutoNoDuration');
  }
  return t('cms.activities.tripRangeEndAutoHelp', { days });
});

const todayYmd = computed(() => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
});

const reduceGuiderOption = (option: GuiderSelectOption | number) => {
  if (typeof option === 'object' && option !== null && 'value' in option) {
    return option.value;
  }
  return option as number;
};

function parseDurationDays(duration: number | string | null | undefined): number | null {
  if (duration === null || duration === undefined || duration === '') {
    return null;
  }
  if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
    return Math.round(duration);
  }
  const match = String(duration).match(/(\d+)/);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function computeEndDateFromStart(startYmd: string, durationDays: number | null): string {
  if (!startYmd || !durationDays || durationDays < 1) {
    return '';
  }
  const parts = startYmd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return '';
  }
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  // Last day of the trip is not counted as an extra calendar day (e.g. 6-day trip: start + 5).
  date.setDate(date.getDate() + durationDays - 1);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function availableForRange(range: CmsTripDateRange): number {
  const max = Math.max(0, Number(range.max_pax) || 0);
  const booked = Math.max(0, Number(range.booked_pax) || 0);
  return Math.max(0, max - booked);
}

function createEmptyRange(): CmsTripDateRange {
  return {
    start_date: '',
    end_date: '',
    base_price_single_room: props.defaultSingleRoomPrice ?? 0,
    base_price_double_room: props.defaultDoubleRoomPrice ?? 0,
    max_pax: 0,
    booked_pax: 0,
    guider_ids: []
  };
}

function patchRange(index: number, patch: Partial<CmsTripDateRange>) {
  const next = props.modelValue.map((r, i) => (i === index ? { ...r, ...patch } : { ...r }));
  emit('update:modelValue', next);
}

function updateField<K extends keyof CmsTripDateRange>(
  index: number,
  key: K,
  value: CmsTripDateRange[K]
) {
  if (
    key === 'base_price_single_room' ||
    key === 'base_price_double_room' ||
    key === 'max_pax'
  ) {
    const num = Number(value);
    patchRange(index, { [key]: Number.isFinite(num) ? num : 0 } as Partial<CmsTripDateRange>);
    return;
  }
  if (key === 'guider_ids') {
    const ids = Array.isArray(value)
      ? value.filter((id): id is number => typeof id === 'number' && id > 0)
      : [];
    patchRange(index, { guider_ids: ids });
    return;
  }
  patchRange(index, { [key]: value } as Partial<CmsTripDateRange>);
}

const updateStart = (index: number, value: string) => {
  if (value && value < todayYmd.value) {
    return;
  }
  const days = parseDurationDays(props.duration);
  const end_date = computeEndDateFromStart(value, days);
  patchRange(index, { start_date: value, end_date });
};

const removeRange = (index: number) => {
  if (props.modelValue.length <= 1) return;
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index)
  );
};

const addRange = () => {
  emit('update:modelValue', [...props.modelValue, createEmptyRange()]);
};

watch(
  () => parseDurationDays(props.duration),
  (days) => {
    if (!days || !props.modelValue.length) return;
    let changed = false;
    const next = props.modelValue.map((r) => {
      if (!r.start_date) return r;
      const end_date = computeEndDateFromStart(r.start_date, days);
      if (end_date === r.end_date) return r;
      changed = true;
      return { ...r, end_date };
    });
    if (changed) {
      emit('update:modelValue', next);
    }
  }
);
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

$cms-date-range-cols: 2rem minmax(7rem, 1fr) minmax(7rem, 1fr) 5.5rem 5.5rem 4rem 3.5rem minmax(12rem, 2fr) 2.25rem;

.cms-date-range-list {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-sm;
}

.cms-date-range-columns,
.cms-date-range-row {
  display: grid;
  grid-template-columns: $cms-date-range-cols;
  gap: $cms-spacing-sm;
  align-items: center;
  min-width: min(100%, 58rem);
}

.cms-date-range-columns {
  padding-bottom: 2px;
}

.cms-date-range-available {
  font-size: $cms-font-size-sm;
  font-weight: 700;
  color: $cms-text-primary;
  text-align: center;
  padding: 0.35rem 0;
}

.cms-date-range-col {
  font-size: $cms-font-size-xs;
  font-weight: 600;
  color: $cms-text-secondary;
  line-height: 1.2;
  min-width: 0;
}

.cms-date-range-col--trip,
.cms-date-range-col--action {
  text-align: center;
}

$cms-date-range-control-height: 38px;

.cms-date-range-row {
  padding: $cms-spacing-xs 0;
  border-bottom: 1px solid $cms-border-color-dark;
  overflow: visible;

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }

  :deep(.cms-input-embedded .cms-input),
  :deep(.cms-date-input-embedded .cms-input) {
    min-height: $cms-date-range-control-height;
    box-sizing: border-box;
  }
}

.cms-date-range-row__trip {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: $cms-radius-sm;
  background: $cms-background-light;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  color: $cms-text-secondary;
}

.cms-date-range-row__guiders {
  min-width: 0;
  overflow: visible;
  align-self: center;

  :deep(.cms-date-range-guider-select) {
    width: 100%;
    --vs-border-color: #{$cms-border-color-dark};
    --vs-border-radius: #{$cms-radius-xs};
    --vs-selected-bg: #{$cms-background-gray};
    --vs-selected-color: #{$cms-text-primary};
    --vs-controls-color: #{$cms-text-secondary};
    --vs-font-size: 13px;
    --vs-line-height: 1.3;

    .vs__dropdown-toggle {
      min-height: $cms-date-range-control-height;
      padding: 0 0 2px;
      border-color: $cms-border-color-dark;
      border-radius: $cms-radius-xs;
      background: $cms-background-white;
      align-items: center;
    }

    .vs__selected-options {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      align-content: center;
      gap: 2px;
      min-height: calc(#{$cms-date-range-control-height} - 4px);
      padding: 2px 4px;
    }

    .vs__selected {
      display: inline-flex;
      align-items: center;
      margin: 0;
      padding: 0 4px 0 8px;
      height: 24px;
      font-size: 12px;
      line-height: 1;
      border-radius: $cms-radius-xs;
      background: $cms-background-gray;
      border: 1px solid $cms-border-color;
      color: $cms-text-primary;
    }

    .vs__deselect {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: $cms-text-secondary !important;
      fill: $cms-background-white !important;
      text-shadow: none;
      cursor: pointer;
      flex-shrink: 0;

      svg {
        width: 8px;
        height: 8px;
      }

      &:hover {
        background: $cms-danger-solid !important;
        fill: $cms-background-white !important;
      }
    }

    .vs__actions {
      display: flex;
      align-items: center;
      padding: 0 6px;
    }

    .vs__search {
      margin: 0;
      padding: 0 4px;
      font-size: 13px;
      line-height: 1.3;
    }

    .vs__open-indicator {
      fill: $cms-text-secondary;
    }
  }
}

.cms-date-range-row__delete {
  justify-self: center;
}

.cms-date-range-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@include cms-responsive-mobile {
  .cms-date-range-columns {
    display: none;
  }

  .cms-date-range-row {
    position: relative;
    grid-template-columns: 1fr 1fr;
    min-width: 0;
    padding: $cms-spacing-lg $cms-spacing-sm $cms-spacing-sm;
    border: 1px solid $cms-border-color-dark;
    border-radius: $cms-radius-sm;
    gap: $cms-spacing-sm;
  }

  .cms-date-range-row__trip {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .cms-date-range-row__guiders {
    grid-column: 1 / -1;
  }

  .cms-date-range-row__delete {
    position: absolute;
    top: $cms-spacing-sm;
    right: $cms-spacing-sm;
  }
}
</style>
